import { getErrorMessage } from '@fruitful/fruitils';
import { router, useLocalSearchParams } from 'expo-router';
import { useAtom } from 'jotai';
import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { Platform } from 'react-native';
import { type SceneRendererProps, TabView } from 'react-native-tab-view';
import {
    BackButton,
    BackButtonIconType,
    BodyLargeEmphasized,
    FullScreenSpinner,
    InfoButton,
    ScreenContainer,
    useToastController,
    View,
    XStack,
} from 'smoothie';

import { isEmpty } from '../../../common/misc';
import { useAuthedUser } from '../../../common/providers/AuthedUserProvider';
import {
    mapAccountColorToCardColor,
    preferLegalName,
} from '../../../common/utils/bankingUtils';
import {
    toCardFriendlyName,
    truncateCardFriendlyName,
} from '../../../common/utils/cardUtils';
import LogRocketFacade from '../../../logger/log-rocket/LogRocketFacade';
import { captureException } from '../../../logger/Logger';
import type { UserAddress } from '../../../user/types/User';
import useIssueCard from '../../hooks/card/useIssueCard';
import useCreateSpace from '../../hooks/useCreateSpace';
import useGetAllBankAccountDetails from '../../hooks/useGetAllBankAccountDetails';
import { preselectedAccountForCardAtom } from '../../state/SpacesAtoms';
import { AccountType, HolisticCategory } from '../../types/BankAccount';
import {
    type Card,
    CardForm,
    CardStatus,
    type IssueCardBody,
} from '../../types/Card';
import {
    FRUITFUL_CARD_ACCOUNT_OPEN_POLICY_DISCLOSURES,
    getBankingDisclosures,
} from '../Disclosures';
import AddFruitfulCard from './steps/AddFruitfulCard';
import { RequestedCardType } from './steps/CardPreference';
import CardRequestDetails from './steps/CardRequestDetails';
import CardSuccess, { CardSuccessAddToWallet } from './steps/CardSuccess';

enum AddCardStep {
    ADD_FRUITFUL_CARD = 'addFruitfulCard',
    CARD_REQUEST_DETAILS = 'cardRequestDetails',
    VIRTUAL_CARD_READY = 'virtualCardReady',
    PHYSICAL_CARD_SENT = 'physicalCardSent',
    MEANTIME_VIRTUAL = 'meantimeVirtual',
}

const ADD_CARD_STEP_TITLE = {
    jointAccountCardInfo: 'Fruitful Card',
    cardholderName: 'Name on card',
    mailingAddress: 'Mailing address',
};

const AddCard = () => {
    LogRocketFacade.tagPage('AddCard');

    const toast = useToastController();
    const user = useAuthedUser();

    const {
        isJointInvitation: isJointInvitationParam,
        isFromJointAccountOpening: isFromJointAccountOpeningParam,
    } = useLocalSearchParams<{
        isJointInvitation?: string;
        isFromJointAccountOpening?: string;
    }>();
    const isJointInvitation = isJointInvitationParam === 'true';
    const isFromJointAccountOpening = isFromJointAccountOpeningParam === 'true';

    const [preselectedAccountForCard, setPreselectedAccountForCard] = useAtom(
        preselectedAccountForCardAtom,
    );

    const {
        mutateAsync: createChargeSecuredAccount,
        isPending: isCreateChargeSecuredAccountPending,
    } = useCreateSpace();
    const { mutateAsync: issueCard, isPending: isIssueCardPending } =
        useIssueCard();

    // Check if user has existing physical cards
    const { bankAccountDetails, refetchAccountDetails } =
        useGetAllBankAccountDetails(false);

    const hasExistingPhysicalCard = useMemo(
        () =>
            bankAccountDetails?.some((account) =>
                account.cards?.some(
                    (card) =>
                        card.form === CardForm.PHYSICAL &&
                        card.syncteraPersonId === user.syncteraPersonId &&
                        card.cardStatus !== CardStatus.TERMINATED,
                ),
            ),
        [JSON.stringify(bankAccountDetails)],
    );

    const routes = Object.values(AddCardStep).map((step) => ({
        key: step,
        title: ADD_CARD_STEP_TITLE[step],
    }));

    const getRouteIndex = (step: AddCardStep) =>
        routes.findIndex(({ key }) => key === step);

    const addFruitfulCardIndex = getRouteIndex(AddCardStep.ADD_FRUITFUL_CARD);
    const cardRequestDetailsIndex = getRouteIndex(
        AddCardStep.CARD_REQUEST_DETAILS,
    );
    const virtualCardReadyIndex = getRouteIndex(AddCardStep.VIRTUAL_CARD_READY);
    const physicalCardSentIndex = getRouteIndex(AddCardStep.PHYSICAL_CARD_SENT);
    const meantimeVirtualIndex = getRouteIndex(AddCardStep.MEANTIME_VIRTUAL);

    const [firstName, lastName] = preferLegalName(user);

    const cardFriendlyFirstName = toCardFriendlyName(firstName);
    const cardFriendlyLastName = toCardFriendlyName(lastName);

    // Truncate legal name to max limit that Synctera accepts
    const cardFriendlyFullName = truncateCardFriendlyName(
        cardFriendlyFirstName,
        cardFriendlyLastName,
    );

    const [index, setIndex] = useState(
        isJointInvitation && isFromJointAccountOpening
            ? cardRequestDetailsIndex
            : 0,
    );
    const [cardData, setCardData] = useState<Partial<IssueCardBody>>({});
    const [requestedCardType, setRequestedCardType] =
        useState<RequestedCardType | null>(null);
    const [enableAutoPay, setEnableAutoPay] = useState<boolean>(
        preselectedAccountForCard?.autoPayEnabled ?? false,
    );
    const [issuedVirtualCard, setIssuedVirtualCard] = useState<Card | null>(
        null,
    );

    const cardColor = mapAccountColorToCardColor(
        preselectedAccountForCard?.color,
    );

    useEffect(() => {
        // For joint account holders issuing cards, preselect based on what the other account holder chose
        if (isJointInvitation && preselectedAccountForCard != null) {
            const hasPhysicalCard = (
                preselectedAccountForCard.cards ?? []
            ).some((card) => card.form === CardForm.PHYSICAL);

            setRequestedCardType(
                hasPhysicalCard && !hasExistingPhysicalCard
                    ? RequestedCardType.VIRTUAL_AND_PHYSICAL
                    : RequestedCardType.VIRTUAL_ONLY,
            );

            preselectedAccountForCard.autoPayEnabled != null &&
                setEnableAutoPay(preselectedAccountForCard.autoPayEnabled);
        }
    }, [isJointInvitation, JSON.stringify(preselectedAccountForCard)]);

    const isRequestingPhysicalCard =
        requestedCardType === RequestedCardType.VIRTUAL_AND_PHYSICAL;

    useEffect(() => {
        const { userAddress, phoneNumber } = user;

        const { line1, line2, city, state, postalCode } =
            userAddress as UserAddress;

        const requiredFields = [
            firstName,
            lastName,
            phoneNumber,
            line1,
            city,
            postalCode,
            state,
        ];
        const allFieldsPresent = requiredFields.every(
            (field) => !isEmpty(field),
        );

        if (allFieldsPresent) {
            setCardData({
                embossName: cardFriendlyFullName,
                ...(requestedCardType ===
                    RequestedCardType.VIRTUAL_AND_PHYSICAL && {
                    shipping: {
                        recipientFirstName: cardFriendlyFirstName,
                        recipientLastName: cardFriendlyLastName,
                        phoneNumber: phoneNumber as string,
                        address: {
                            line1,
                            ...(line2 != null && !isEmpty(line2) && { line2 }),
                            city,
                            state: state as string,
                            postalCode,
                            countryCode: 'US',
                        },
                    },
                }),
            });
        }
    }, [user, requestedCardType]);

    const onSubmit = async (autopayEnabled: boolean): Promise<void> => {
        if (cardData == null) {
            toast.show(
                'Missing required information to add a card. Please contact Fruitful Hospitality.',
            );
            return;
        }

        const { embossName, shipping } = cardData as IssueCardBody;
        try {
            if (!isEmpty(preselectedAccountForCard?.syncteraAccountId)) {
                let chargeSecuredAccountId =
                    preselectedAccountForCard?.chargeSecuredSyncteraAccountId;

                if (chargeSecuredAccountId == null) {
                    const createdAccount = await createChargeSecuredAccount({
                        body: {
                            userId: user.id,
                            accountType: AccountType.CHARGE_SECURED,
                            enableAutoPay: autopayEnabled,
                            holisticCategory: HolisticCategory.SPEND, // As of 2024-12-19 account not used by holistic view. If this becomes relevant we would likely want this value to be kept in sync with the linked Cash account
                            nickname: `Card - ${preselectedAccountForCard?.nickname}`,
                            linkedAccountId:
                                preselectedAccountForCard?.syncteraAccountId as string, // The account we want to issue cards for should have been pre-selected after account opening in the KYC flow or after creating a new space
                            disclosures: getBankingDisclosures({
                                enableAutoPay: autopayEnabled ?? false,
                                policyTypes:
                                    FRUITFUL_CARD_ACCOUNT_OPEN_POLICY_DISCLOSURES,
                            }),
                        },
                    });

                    chargeSecuredAccountId = createdAccount.syncteraAccountId;
                }

                const virtualAccountDetail = await issueCard({
                    body: {
                        form: CardForm.VIRTUAL,
                        syncteraAccountId: chargeSecuredAccountId,
                        embossName,
                        cardColor,
                    },
                });
                setIssuedVirtualCard(virtualAccountDetail);

                try {
                    if (isRequestingPhysicalCard) {
                        await issueCard({
                            body: {
                                form: CardForm.PHYSICAL,
                                syncteraAccountId: chargeSecuredAccountId,
                                embossName,
                                shipping,
                            },
                        });
                    }
                } catch (e) {
                    if (
                        getErrorMessage(e)?.includes(
                            'reached limit for physical card',
                        )
                    ) {
                        toast.show(
                            'You have reached your limit for physical cards',
                        );
                    } else {
                        throw e;
                    }
                }

                if (isRequestingPhysicalCard) {
                    setIndex(physicalCardSentIndex);
                } else {
                    setIndex(virtualCardReadyIndex);
                }
            } else {
                captureException(
                    new Error(
                        'Error issuing cards: syncteraAccountId not provided',
                    ),
                );
                router.navigate('/home/banking');
                toast.show('Unable to issue your cards');
            }
        } catch (e) {
            captureException(e);
            router.navigate('/home/banking');
            toast.show('Unable to issue your cards');
        } finally {
            setPreselectedAccountForCard(null);
            void refetchAccountDetails();
        }
    };

    const renderScene = ({
        route,
    }: SceneRendererProps & { route: { key: AddCardStep } }) => {
        if (routes[index]?.key !== route.key) {
            return null;
        }

        switch (route.key) {
            case AddCardStep.ADD_FRUITFUL_CARD:
                return (
                    <AddFruitfulCard
                        name={[firstName, lastName]}
                        onPressAddCard={(cardType: RequestedCardType) => {
                            setRequestedCardType(cardType);
                            setIndex(cardRequestDetailsIndex);
                        }}
                        onPressSkip={() => {
                            router.replace('/home/banking');
                        }}
                        hasExistingPhysicalCard={hasExistingPhysicalCard}
                    />
                );
            case AddCardStep.CARD_REQUEST_DETAILS:
                return (
                    <CardRequestDetails
                        autopayEnabled={enableAutoPay}
                        cardColor={cardColor}
                        cardType={requestedCardType}
                        isRequestingPhysicalCard={isRequestingPhysicalCard}
                        onSubmit={onSubmit}
                        preselectedAccountForCard={preselectedAccountForCard}
                        user={user}
                    />
                );
            case AddCardStep.VIRTUAL_CARD_READY:
                return (
                    <>
                        {Platform.OS === 'ios' ? (
                            <CardSuccessAddToWallet
                                cardColor={cardColor}
                                cardForm={CardForm.VIRTUAL}
                                cardId={issuedVirtualCard?.id ?? ''}
                                cardholderName={cardData?.embossName ?? ''}
                                description={
                                    'Start spending today by adding your new Fruitful Card to your Apple Wallet\u2122 now.'
                                }
                                embossName={
                                    issuedVirtualCard?.embossName ?? {
                                        line1: '',
                                        line2: '',
                                    }
                                }
                                headerText={
                                    'Your virtual card is\nready to go!'
                                }
                                lastFour={issuedVirtualCard?.lastFour ?? ''}
                                onSuccess={() => {
                                    router.navigate('/home/banking');
                                }}
                                onError={() => {
                                    router.navigate('/home/banking');
                                }}
                            />
                        ) : (
                            <CardSuccess
                                cardholderName={cardData?.embossName ?? ''}
                                cardColor={cardColor}
                                cardForm={CardForm.VIRTUAL}
                                onPress={() => {
                                    router.navigate('/home/banking');
                                }}
                                buttonText="Done"
                                headerText={
                                    'Your virtual card is\nready to go!'
                                }
                                description={
                                    'Open the Apple Wallet\u2122 or Google Pay\u2122\napp to add your new Fruitful Card to your\ndigital wallet now.'
                                }
                            />
                        )}
                    </>
                );
            case AddCardStep.PHYSICAL_CARD_SENT:
                return (
                    <>
                        {Platform.OS === 'ios' ? (
                            <CardSuccessAddToWallet
                                cardColor={cardColor}
                                cardForm={CardForm.PHYSICAL}
                                cardId={issuedVirtualCard?.id ?? ''}
                                cardholderName={cardData?.embossName ?? ''}
                                description={
                                    'Your physical card should arrive in 5-7 business days, but you can start spending today by adding your virtual Fruitful Card to your Apple Wallet\u2122 now.'
                                }
                                embossName={
                                    issuedVirtualCard?.embossName ?? {
                                        line1: '',
                                        line2: '',
                                    }
                                }
                                headerText="Your Fruitful Card is ready!"
                                lastFour={issuedVirtualCard?.lastFour ?? ''}
                                onSuccess={() => {
                                    router.navigate('/home/banking');
                                }}
                                onError={() => {
                                    router.navigate('/home/banking');
                                }}
                            />
                        ) : (
                            <CardSuccess
                                cardholderName={cardData?.embossName ?? ''}
                                cardColor={cardColor}
                                cardForm={CardForm.PHYSICAL}
                                onPress={() => {
                                    setIndex(meantimeVirtualIndex);
                                }}
                                buttonText="Continue"
                                headerText={
                                    'Your physical card is on\nthe way!'
                                }
                                description="You should get it in 5-7 business days."
                            />
                        )}
                    </>
                );
            case AddCardStep.MEANTIME_VIRTUAL:
                return (
                    <CardSuccess
                        cardholderName={cardData?.embossName ?? ''}
                        cardColor={cardColor}
                        cardForm={CardForm.VIRTUAL}
                        onPress={() => {
                            router.navigate('/home/banking');
                        }}
                        buttonText="Done"
                        headerText={'In the meantime, use\nyour virtual card.'}
                        description={
                            'Open your Apple Wallet\u2122 or Google Pay\u2122\napp to add your virtual Fruitful Card to your\ndigital wallet now.'
                        }
                    />
                );

            default:
                return <></>;
        }
    };

    const onPressBack = () => {
        if (router.canGoBack()) {
            router.back();
        } else {
            router.replace('/home/banking');
        }
    };

    const isNotWaitingForCardIndex = ![
        virtualCardReadyIndex,
        physicalCardSentIndex,
        meantimeVirtualIndex,
    ].includes(index);
    const isCardDetailsFromJointOpening =
        isFromJointAccountOpening && index === cardRequestDetailsIndex;
    const showBackButton =
        isNotWaitingForCardIndex && !isCardDetailsFromJointOpening;

    const renderTabBar = () => {
        return (
            <View height={48} paddingTop={8}>
                {index !== addFruitfulCardIndex ? (
                    <XStack alignItems="center">
                        <View alignItems="center">
                            {showBackButton && (
                                <BackButton
                                    customBackFn={onPressBack}
                                    fallbackRoute="/home/banking"
                                    iconType={BackButtonIconType.ARROW_LEFT}
                                />
                            )}
                        </View>
                        <BodyLargeEmphasized
                            textAlign="center"
                            flexGrow={1}
                            marginRight={25}
                        >
                            {routes?.[index]?.title}
                        </BodyLargeEmphasized>
                        <View alignItems="center">
                            {[
                                virtualCardReadyIndex,
                                physicalCardSentIndex,
                                meantimeVirtualIndex,
                            ].includes(index) && (
                                <BackButton
                                    customBackFn={() => {
                                        router.navigate('/home/banking');
                                    }}
                                    alignSelf="flex-end"
                                    iconType={BackButtonIconType.X}
                                />
                            )}
                        </View>
                    </XStack>
                ) : (
                    <XStack alignItems="center" justifyContent="flex-end">
                        <InfoButton
                            onPress={() => {
                                router.navigate('/settings/faq');
                            }}
                        />
                    </XStack>
                )}
            </View>
        );
    };

    const isLoading = isIssueCardPending || isCreateChargeSecuredAccountPending;

    if (isLoading) {
        return <FullScreenSpinner />;
    }

    return (
        <ScreenContainer>
            <View height="100%">
                <TabView
                    swipeEnabled={false}
                    navigationState={{ index, routes }}
                    renderScene={renderScene}
                    onIndexChange={setIndex}
                    renderTabBar={renderTabBar}
                />
            </View>
        </ScreenContainer>
    );
};

export default AddCard;
